export const RELATIONSHIPS = ['妻', '夫', '父', '母', '子', '兄弟、姉妹', 'その他'] as const;
export type ProfileEditor = 'email' | 'loginId' | 'password' | 'emergency';
export type ProfilePhase = 'idle' | 'checking' | 'saving' | 'refreshing' | 'failed' | 'unknown' | 'saved' | 'saved-stale';
export interface ProfileOperation { readonly kind?: ProfileEditor; readonly phase: ProfilePhase; readonly message: string; }
export interface EmergencyContact {
  emLastName: string;
  emFirstName: string;
  emLastNameKana: string;
  emFirstNameKana: string;
  emRelationship: number | null;
  emPhone: string;
}
export interface MemberProfile extends EmergencyContact {
  id: number;
  studioId: number;
  memberCode: string;
  loginId: string;
  email: string;
  zip: string;
  province: string;
  city: string;
  area: string;
  address: string;
  phone: string;
  studio: { name: string };
  studioPlan: { name: string };
  payment: { paymentMethod: number | null };
}
export type ProfileCommand =
  | { kind: 'email'; value: string }
  | { kind: 'loginId'; value: string }
  | { kind: 'password'; current: string; next: string; confirmation: string }
  | { kind: 'emergency'; value: EmergencyContact };

export const EDITOR_TITLES: Record<ProfileEditor, string> = {
  email: 'メールアドレス編集', loginId: 'ログインID編集', password: 'パスワード編集', emergency: '緊急連絡先編集',
};
export const SUCCESS_MESSAGES: Record<ProfileEditor, string> = {
  email: 'メールアドレスを更新しました', loginId: 'ログインIDを更新しました',
  password: 'パスワードを更新しました', emergency: '緊急連絡先を更新しました',
};
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export function parseMemberProfile(value: unknown): MemberProfile | null {
  if (!isRecord(value) || !Number.isInteger(value['id']) || Number(value['id']) <= 0 ||
      !Number.isInteger(value['studioId']) || typeof value['loginId'] !== 'string' || !value['loginId']) return null;
  const fields = ['memberCode', 'email', 'zip', 'province', 'city', 'area', 'address', 'phone',
    'emLastName', 'emFirstName', 'emLastNameKana', 'emFirstNameKana', 'emPhone'] as const;
  const text: Record<string, string> = {};
  for (const field of fields) {
    if (value[field] != null && typeof value[field] !== 'string') return null;
    text[field] = (value[field] as string | null | undefined) ?? '';
  }
  const name = (item: unknown): string | null => item == null ? '' :
    isRecord(item) && (item['name'] == null || typeof item['name'] === 'string') ? (item['name'] as string ?? '') : null;
  const studio = name(value['studio']), plan = name(value['studioPlan']);
  if (studio === null || plan === null) return null;
  const relationship = value['emRelationship'] == null ? null : Number(value['emRelationship']);
  if (relationship !== null && (!Number.isInteger(relationship) || relationship < 0 || relationship >= RELATIONSHIPS.length)) return null;
  const payment = value['payment'];
  if (payment != null && !isRecord(payment)) return null;
  const method = isRecord(payment) && payment['paymentMethod'] != null ? Number(payment['paymentMethod']) : null;
  if (method !== null && !Number.isInteger(method)) return null;
  return { ...text, id: Number(value['id']), studioId: Number(value['studioId']), loginId: value['loginId'],
    studio: { name: studio }, studioPlan: { name: plan }, payment: { paymentMethod: method },
    emRelationship: relationship } as MemberProfile;
}

export function loginIdPattern(existing: string): RegExp {
  const escaped = existing.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^(?:${escaped}|[0-9a-zA-Z]+[0-9a-zA-Z.\\-_]{0,199})$`);
}

export function validateProfileCommand(command: ProfileCommand, member: MemberProfile): string | null {
  if (command.kind === 'email' && !/^[^\s@]+@[^\s@]+$/.test(command.value)) return 'メールアドレスを正しく入力してください。';
  if (command.kind === 'loginId' && (!command.value || !loginIdPattern(member.loginId).test(command.value))) return 'ログインIDを正しく入力してください。';
  if (command.kind === 'password') {
    if (!command.current || command.next.length < 8 || command.next.length > 16) return 'パスワードは8～16文字で入力してください。';
    if (command.next !== command.confirmation) return '新しいパスワードと確認は同じものを入力してください';
  }
  if (command.kind === 'emergency') {
    const v = command.value;
    if (![v.emLastName, v.emFirstName, v.emLastNameKana, v.emFirstNameKana].every(Boolean) ||
        v.emRelationship === null || !Number.isInteger(v.emRelationship) || v.emRelationship < 0 || v.emRelationship >= RELATIONSHIPS.length ||
        !/^[\x01-\x7e]+-[\x01-\x7e]+-[\x01-\x7e]+$/.test(v.emPhone)) return '緊急連絡先の必須項目を入力してください。';
  }
  return null;
}

export function profileUpdateBody(id: number, command: ProfileCommand): Record<string, string | number | boolean | null> {
  switch (command.kind) {
    case 'email': return { id, email: command.value };
    case 'loginId': return { id, loginId: command.value };
    case 'password': return { id, password: command.next, changePassFromMyPage: true };
    case 'emergency': return { id, ...command.value };
  }
}
